import { useCallback, useEffect, useRef, useState } from "react";
import { ExportFormat } from "./useEntityExport";

export type ExportFn = (format: ExportFormat) => void;

// Shared shape for the prop a table component uses to hand its export
// function and in-flight state up to a parent page's toolbar menu.
export type ExportStateChangeHandler = (
  exportTo: ExportFn,
  isExporting: boolean
) => void;

// Wires a table's own useEntityExport output into its onExportStateChange
// prop. Extracted so tables don't each repeat the same effect + prop type.
export const useNotifyExportState = (
  onExportStateChange: ExportStateChangeHandler | undefined,
  exportTo: ExportFn,
  isExporting: boolean
) => {
  useEffect(() => {
    onExportStateChange?.(exportTo, isExporting);
  }, [onExportStateChange, exportTo, isExporting]);
};

// Bridges export state from a child table (which owns useEntityExport) up to
// a parent page's toolbar menu, via the table's onExportStateChange prop.
export const useExportButtonState = () => {
  const exportFnRef = useRef<ExportFn | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportStateChange = useCallback(
    (exportTo: ExportFn, exporting: boolean) => {
      exportFnRef.current = exportTo;
      setIsExporting(exporting);
    },
    []
  );

  const exportTo = useCallback((format: ExportFormat) => {
    exportFnRef.current?.(format);
  }, []);

  return { exportTo, isExporting, handleExportStateChange };
};
